import React, { useState } from "react";
import { LOGO_DEV_TOKEN } from "../config/site";

/**
 * CompanyLogo — renders a company logo from logo.dev by domain, with the
 * company name shown below (edynoor-style). If logo.dev has no logo or the
 * request fails, it falls back to showing just the company name in a chip.
 *
 * Usage:
 *   <CompanyLogo domain="oracle.com" name="Oracle" />
 *   <CompanyLogo domain="zoho.com" name="Zoho" size={96} />
 *
 * Requires REACT_APP_LOGODEV_TOKEN to be set for logos to load; otherwise
 * it gracefully shows the name chip only.
 */
export default function CompanyLogo({
  domain,
  name,
  size = 80,
  showName = true,
}) {
  const [failed, setFailed] = useState(false);
  const canLoad = Boolean(LOGO_DEV_TOKEN) && Boolean(domain) && !failed;

  const src = canLoad
    ? `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=${size * 2}&format=png&retina=true`
    : null;

  return (
    <div
      className="company-logo"
      title={name}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: 12,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={`${name} logo`}
          loading="lazy"
          onError={() => setFailed(true)}
          style={{
            width: size,
            height: size,
            objectFit: "contain",
            borderRadius: 8,
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          style={{
            width: size,
            height: size,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            background: "#f1f5f9",
            color: "#475569",
            fontWeight: 700,
            fontSize: size * 0.32,
          }}
        >
          {(name || "?").charAt(0).toUpperCase()}
        </div>
      )}
      {showName && (
        <span
          className="company-logo-name"
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "#475569",
            textAlign: "center",
          }}
        >
          {name}
        </span>
      )}
    </div>
  );
}
